/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useCallback, useRef, MouseEvent } from "react";
import { FC_ANCHOR } from "../FloatingContainer";
import { Tooltip } from "../Tooltip";

export interface IFieldSwitchProps {
	value: boolean;
	disabled?: boolean;
	className?: string;
	tooltip?: string;
	readOnly?: boolean;
	onChange?: (value: boolean) => void;
}

export const FieldSwitch: FC<IFieldSwitchProps> = ({
	value,
	disabled,
	className,
	tooltip,
	onChange,
	readOnly
}) => {
	const controlElRef = useRef<HTMLButtonElement>();

	const handleButtonClick = useCallback(
		(ev: MouseEvent) => {
			if (readOnly) {
				return;
			}

			ev.stopPropagation();
			onChange(!value);
		},
		[ value, onChange, readOnly ]
	);

	const baseClass = BEM("edt-field-switch", null, {
		"state-on": value,
		"state-off": !value,
		readonly: readOnly
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<button
			className={baseClass.toClassName()}
			disabled={disabled || readOnly}
			ref={controlElRef}
			onClick={handleButtonClick}
		>
			<span className={baseClass.element("circle").toClassName()} />
			{tooltip ? (
				<Tooltip for={controlElRef} anchor={FC_ANCHOR.CENTER_TOP} openDelay={1200}>
					{tooltip}
				</Tooltip>
			) : null}
		</button>
	);
};
